import { useEffect, useState } from 'react';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import buildCopy from '@/i18n/ns/build-experience';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { OriginTag, StatusPill } from '@/components/ui/kit';
import { Callout } from '@/components/ui/content';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import { WorkspaceIcon } from '../icons';
import { useTimeLabel } from './format';
import type { CompareState, VersionRowVM, VersionsPanelVM } from './types';

// The Versions panel: every version with who made it and whether it was
// checked, a compare between two versions, and Restore. The diff is computed
// by the host and arrives as rows bound to the exact pair they were computed
// for; a result for any other pair is never drawn. Restore makes a new version
// and asks first, and its confirmation only exists while the current model
// still allows it. Nothing here reads or writes: callbacks do.

export interface VersionsPanelProps {
  vm: VersionsPanelVM;
  /** The caller's compare result; drawn only when it matches the chosen pair. */
  compare?: CompareState;
  /** Seed the chosen pair (gallery and tests); the person's picks replace it. */
  defaultPair?: { from: string; to: string };
  onCompare?: (fromRevisionId: string, toRevisionId: string) => void;
  onCloseCompare?: () => void;
  onRestore?: (revisionId: string) => void;
  onRetry?: () => void;
  /**
   * A row is identified by its `versionId` when the history reader names one, else by its
   * `revisionId`. That row key is what `onRestore`, `onCompare`, `defaultPair` and
   * `restore.revisionId` carry, whatever their parameter is called.
   */
  /** Opens the supplied named row; the host independently rechecks its reader. */
  onOpen?: (version: VersionRowVM) => void;
  /**
   * The `versionId` the host is already displaying. That row offers no Open: it would only reopen
   * what is on screen. It is the displayed version, not the head, so `version.current` never hides it.
   */
  selectedVersionId?: string;
}

const versionKey = (version: VersionRowVM) => version.versionId ?? version.revisionId;

const CAUSE_KEY = {
  person: 'versionCausePerson',
  agent: 'versionCauseAgent',
  refresh: 'versionCauseRefresh',
  restore: 'versionCauseRestore',
  import: 'versionCauseImport',
} as const;
const ORIGIN_KEY = {
  native: 'versionOriginNative',
  mcp: 'versionOriginMcp',
  terminal: 'versionOriginTerminal',
  job: 'versionOriginJob',
} as const;
const CHECKED_KEY = {
  yes: 'versionCheckedYes',
  no: 'versionCheckedNo',
  invalidated: 'versionCheckedInvalidated',
} as const;

export function VersionsPanel({
  vm,
  compare,
  defaultPair,
  onCompare,
  onCloseCompare,
  onRestore,
  onRetry,
  onOpen,
  selectedVersionId,
}: VersionsPanelProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const [base, setBase] = useState<string | null>(defaultPair?.from ?? null);
  const [target, setTarget] = useState<string | null>(defaultPair?.to ?? null);
  // The confirmation is bound to the version AND the current head it was opened at.
  const [confirming, setConfirmingState] = useState<{ id: string; head: string | null } | null>(
    null,
  );
  const ids = new Set(vm.versions.map(versionKey));
  const byId = new Map(vm.versions.map((v) => [versionKey(v), v]));

  // A chosen version that left the list can no longer be compared.
  const pairValid = base !== null && ids.has(base) && (target === null || ids.has(target));
  useEffect(() => {
    if (!pairValid && (base !== null || target !== null)) {
      setBase(null);
      setTarget(null);
    }
  }, [pairValid, base, target]);

  // The confirmation is valid only for a restorable, non-current, existing version.
  const headVersion = vm.versions.find((v) => v.current);
  const head = headVersion ? versionKey(headVersion) : null;
  const setConfirming = (id: string | null) =>
    setConfirmingState(id === null ? null : { id, head });
  const confirmed = confirming ? (byId.get(confirming.id) ?? null) : null;
  // Any change of the current head discards it, so a stale OK can never restore.
  const confirmValid =
    confirming !== null &&
    confirming.head === head &&
    confirmed !== null &&
    vm.canRestore &&
    !confirmed.current &&
    vm.restore.status !== 'pending';
  useEffect(() => {
    if (confirming !== null && !confirmValid) setConfirmingState(null);
  }, [confirming, confirmValid]);

  const restoreFor = (revisionId: string) =>
    vm.restore.status !== 'idle' && vm.restore.revisionId === revisionId ? vm.restore : null;
  const failed = vm.restore.status === 'failed' ? vm.restore : null;
  const pair = pairValid && base !== null && target !== null ? { from: base, to: target } : null;
  // Only a result computed for exactly this pair is drawn.
  const result =
    pair && compare && compare.from === pair.from && compare.to === pair.to ? compare : null;

  const pickBase = (revisionId: string) => {
    setBase(base === revisionId ? null : revisionId);
    setTarget(null);
    onCloseCompare?.();
  };
  const pickTarget = (revisionId: string) => {
    if (base === null) return;
    setTarget(revisionId);
    onCompare?.(base, revisionId);
  };

  return (
    <section className="ap-panel" aria-labelledby="ap-versions-title" data-panel="versions">
      <header className="ap-head">
        <h2 id="ap-versions-title">{copy.versionsTitle}</h2>
      </header>
      {failed && (
        <Callout tone={failed.reason === 'stale' ? 'warning' : 'danger'}>
          {failed.reason === 'stale'
            ? copy.restoreStale
            : failed.reason === 'denied'
              ? copy.restoreDenied
              : copy.restoreFailed}
        </Callout>
      )}
      {vm.load === 'loading' && <LoadingState label={copy.versionsLoading} />}
      {vm.load === 'error' && <ErrorState message={copy.versionsErrorBody} onRetry={onRetry} />}
      {vm.load === 'ready' && vm.versions.length === 0 && (
        <EmptyState
          icon="history"
          title={copy.versionsEmptyTitle}
          description={copy.versionsEmptyBody}
        />
      )}
      {vm.load === 'ready' && vm.versions.length > 0 && (
        <ol className="ap-list">
          {vm.versions.map((version) => (
            <VersionRow
              key={versionKey(version)}
              version={version}
              mark={
                pair?.from === versionKey(version)
                  ? 'from'
                  : pair?.to === versionKey(version)
                    ? 'to'
                    : null
              }
              isBase={base === versionKey(version)}
              baseNumber={base !== null ? (byId.get(base)?.number ?? null) : null}
              canCompare={vm.versions.length > 1 && onCompare !== undefined}
              canRestore={vm.canRestore}
              restore={restoreFor(versionKey(version))}
              confirming={confirmValid && confirming?.id === versionKey(version)}
              onPickBase={() => pickBase(versionKey(version))}
              onPickTarget={() => pickTarget(versionKey(version))}
              onAskRestore={() => setConfirming(versionKey(version))}
              onOpen={
                onOpen &&
                !(selectedVersionId !== undefined && version.versionId === selectedVersionId)
                  ? () => onOpen(version)
                  : undefined
              }
              onCancelRestore={() => setConfirming(null)}
              onRestore={() => {
                // Guard against the current model, not the click that opened it.
                if (!confirmValid || confirming?.id !== versionKey(version)) return;
                setConfirming(null);
                onRestore?.(versionKey(version));
              }}
            />
          ))}
        </ol>
      )}
      {base !== null && target === null && <p className="ap-note">{copy.comparePick}</p>}
      {pair && (
        <CompareView
          from={byId.get(pair.from)?.number ?? 0}
          to={byId.get(pair.to)?.number ?? 0}
          result={result}
          onClose={() => {
            setBase(null);
            setTarget(null);
            onCloseCompare?.();
          }}
        />
      )}
    </section>
  );
}

function VersionRow({
  version,
  mark,
  isBase,
  baseNumber,
  canCompare,
  canRestore,
  restore,
  confirming,
  onPickBase,
  onPickTarget,
  onAskRestore,
  onCancelRestore,
  onRestore,
  onOpen,
}: {
  version: VersionRowVM;
  mark: 'from' | 'to' | null;
  isBase: boolean;
  baseNumber: number | null;
  canCompare: boolean;
  canRestore: boolean;
  restore: VersionsPanelVM['restore'] | null;
  confirming: boolean;
  onPickBase: () => void;
  onPickTarget: () => void;
  onAskRestore: () => void;
  onCancelRestore: () => void;
  onRestore: () => void;
  onOpen?: () => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const build = useNamespaceCopy(buildCopy);
  const time = useTimeLabel();
  const number = String(version.number);
  const status = restore?.status;
  return (
    <li className="ap-row ap-version" data-current={version.current} data-mark={mark ?? undefined}>
      {version.thumbnailUrl ? (
        <img className="ap-thumb" src={version.thumbnailUrl} alt="" loading="lazy" />
      ) : (
        <span className="ap-thumb" aria-hidden="true">
          <WorkspaceIcon name="results" size={18} />
        </span>
      )}
      <div className="ap-row-copy">
        <strong>
          {formatCopy(copy.versionLabel, { number })}
          {version.current && <StatusPill tone="success">{copy.versionCurrent}</StatusPill>}
          {version.published && <StatusPill>{copy.versionPublished}</StatusPill>}
          {mark && (
            <StatusPill>{mark === 'from' ? copy.compareFromMark : copy.compareToMark}</StatusPill>
          )}
        </strong>
        <span>
          {version.cause === 'build' ? build.built : copy[CAUSE_KEY[version.cause]]}
          {version.cause === 'build' &&
            version.modelLabel &&
            ` · ${formatCopy(build.builtWith, { model: version.modelLabel })}`}
          {version.origin !== 'native' && (
            <>
              {' · '}
              <OriginTag label={copy[ORIGIN_KEY[version.origin]]} />
            </>
          )}
        </span>
        <span>
          {formatCopy(copy.versionBy, { actor: version.actorLabel })} ·{' '}
          {time(version.createdAt) ?? ''}
        </span>
        {version.checked !== null && (
          <span data-checked={version.checked}>{copy[CHECKED_KEY[version.checked]]}</span>
        )}
        {status === 'done' && <span role="status">{formatCopy(copy.restored, { number })}</span>}
        {confirming && (
          <div
            className="ap-confirm"
            role="group"
            aria-label={formatCopy(copy.restoreConfirmTitle, { number })}
          >
            <p>
              <strong>{formatCopy(copy.restoreConfirmTitle, { number })}</strong>{' '}
              {formatCopy(copy.restoreConfirmBody, { number })}
            </p>
            <button type="button" className="ws-button" onClick={onRestore}>
              {copy.restoreConfirm}
            </button>
            <button type="button" className="ws-text-button" onClick={onCancelRestore}>
              {copy.restoreCancel}
            </button>
          </div>
        )}
      </div>
      <div className="ap-row-actions">
        {onOpen && (
          <button type="button" className="ws-text-button" onClick={onOpen}>
            {formatCopy(copy.versionOpen, { number })}
          </button>
        )}
        {baseNumber !== null && !isBase ? (
          <button type="button" className="ws-text-button" onClick={onPickTarget}>
            {formatCopy(copy.compareWith, { number: String(baseNumber) })}
          </button>
        ) : (
          canCompare && (
            <button
              type="button"
              className="ws-text-button"
              aria-pressed={isBase}
              onClick={onPickBase}
            >
              {copy.compareStart}
            </button>
          )
        )}
        {canRestore && !version.current && (
          <button
            type="button"
            className="ws-text-button"
            disabled={status === 'pending'}
            onClick={onAskRestore}
          >
            {status === 'pending' ? copy.restoring : copy.restore}
          </button>
        )}
      </div>
    </li>
  );
}

function CompareView({
  from,
  to,
  result,
  onClose,
}: {
  from: number;
  to: number;
  result: CompareState | null;
  onClose: () => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  return (
    <div
      className="ap-compare"
      role="region"
      aria-live="polite"
      aria-label={formatCopy(copy.compareTitle, { from: String(from), to: String(to) })}
    >
      <h3>{formatCopy(copy.compareTitle, { from: String(from), to: String(to) })}</h3>
      {(result === null || result.status === 'loading') && <p>{copy.compareLoading}</p>}
      {result?.status === 'error' && <ErrorState message={copy.compareError} />}
      {result?.status === 'ready' && result.rows.length === 0 && <p>{copy.compareNone}</p>}
      {result?.status === 'ready' &&
        result.rows.map((row) => (
          <p key={row.id} className="ap-diff" data-change={row.change}>
            <strong>{row.label}</strong>{' '}
            <StatusPill>
              {row.change === 'added'
                ? copy.compareAdded
                : row.change === 'removed'
                  ? copy.compareRemoved
                  : copy.compareChanged}
            </StatusPill>
            {row.before !== null && <del>{row.before}</del>}{' '}
            {row.after !== null && <ins>{row.after}</ins>}
          </p>
        ))}
      <button type="button" className="ws-text-button" onClick={onClose}>
        {copy.close}
      </button>
    </div>
  );
}
